import { Colors, Icon } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import type { PillarSummary } from '@/types/objectives';

import { PLACEHOLDER_BONE_COLOR } from '@/components/objectives/objectiveStyles';
import PillarIcon from '@/components/objectives/PillarIcon';
import PillarStatusMention from '@/components/objectives/PillarStatusMention';
import {
    derivePillarTileGradient,
    needsReadabilityOverlay,
} from '@/components/objectives/pillarSurface';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import i18n from '@/plugins/i18n';
import { resolveCompanyValueIconBackground } from '@/utils/companyValueIconBackground';
import { getLocalizedText } from '@/utils/localization';
import { formatPercent } from '@/utils/numberFormat';
import { clampPercent } from '@/utils/objectiveProgress';

const HERO_ICON_SIZE = 40;

/** The banner's frame and its icon box, shared with its placeholder so both are the same size. */
const HERO_SURFACE = 'obj-pillar-hero relative overflow-hidden rounded-[16px]';
const HERO_ICON_BOX = 'h-[65px] w-[65px] flex-shrink-0 rounded-[10px]';

/** The banner's three counters. */
const PLACEHOLDER_STAT_COUNT = 3;

interface HeroStatPillProps {
    icon: 'cryptocompany-target' | 'users-people' | 'business-trend-up';
    label: string;
}

/**
 * An icon in a white/30 rounded square, then the label. The icon needs `color={Colors.WHITE}`
 * explicitly -- `color={'inherit'}` makes `Icon` skip its own color style entirely and fall back
 * to `currentColor`, which here inherits the page's dark text color rather than white, leaving the
 * glyph nearly invisible against the hero's gradient. The big pillar icon above already gets its
 * white treatment the same way.
 */
function HeroStatPill({ icon, label }: HeroStatPillProps) {
    return (
        <div className={'flex items-center gap-2'}>
            <div
                className={
                    'flex h-[32px] w-[32px] flex-shrink-0 items-center justify-center rounded-lg bg-white/30'
                }
            >
                <Icon
                    name={icon}
                    size={'sm'}
                    color={Colors.WHITE}
                />
            </div>

            <span className={'text-[14px] font-medium text-white'}>{label}</span>
        </div>
    );
}

/** The coloured banner of a pillar page: its identity, its three counters and its overall bar. */
export default function PillarObjectivesHero({ pillar }: { pillar: PillarSummary }) {
    const { t } = useTranslation();

    const displayName = getLocalizedText(pillar.name);
    const displayDescription = getLocalizedText(pillar.description);
    const heroBackground = derivePillarTileGradient(
        resolveCompanyValueIconBackground(pillar.icon, pillar.iconBackground),
    );
    const clampedProgression = clampPercent(pillar.progression);
    // The gradient is deep and saturated enough that most heroes need no scrim -- but an admin
    // can still pick a very pale base colour, so the luminance check stays.
    const overlayClassName = needsReadabilityOverlay(heroBackground)
        ? ' before:absolute before:inset-0 before:rounded-[inherit] before:bg-black/25'
        : '';

    return (
        <section
            style={{ background: heroBackground }}
            className={`${HERO_SURFACE} backdrop-blur-[2px]${overlayClassName}`}
            data-testid={'pillar-objectives-hero'}
        >
            <div className={'obj-pillar-hero-row relative'}>
                <div className={`${HERO_ICON_BOX} flex items-center justify-center bg-white/45`}>
                    <PillarIcon
                        icon={pillar.icon}
                        size={HERO_ICON_SIZE}
                    />
                </div>

                <div className={'min-w-60 flex-1'}>
                    <div className={'mb-1 flex flex-wrap items-center gap-2'}>
                        <p
                            className={
                                'text-[11px] font-medium tracking-wide text-white/80 uppercase'
                            }
                        >
                            {t('objectives.pillarPage.strategicPillar')}
                        </p>

                        <PillarStatusMention
                            status={pillar.status}
                            tone={'onColor'}
                            dataTestId={'pillar-objectives-inactive'}
                        />
                    </div>

                    <h1 className={'obj-pillar-hero-title leading-tight font-medium text-white'}>
                        {displayName}
                    </h1>

                    {displayDescription && (
                        <p className={'mt-2 max-w-3xl text-[14px] text-white/90'}>
                            {displayDescription}
                        </p>
                    )}

                    <div className={'obj-pillar-stats-row mt-6 flex flex-wrap items-center'}>
                        <HeroStatPill
                            icon={'cryptocompany-target'}
                            label={t('objectives.pillars.objectivesCount', {
                                count: pillar.objectivesCount,
                            })}
                        />

                        <HeroStatPill
                            icon={'users-people'}
                            label={t('objectives.pillars.participantsCount', {
                                count: pillar.participantsCount,
                            })}
                        />

                        <HeroStatPill
                            icon={'business-trend-up'}
                            label={t('objectives.pillars.progressionValue', {
                                value: clampedProgression,
                            })}
                        />
                    </div>
                </div>
            </div>

            <div className={'relative mt-8'}>
                <div className={'mb-2 flex items-end justify-between gap-2'}>
                    <span className={'text-[12px] font-medium text-white/90'}>
                        {t('objectives.pillarPage.overallProgress')}
                    </span>

                    <span
                        className={
                            'obj-pillar-progress-value leading-none font-semibold text-white'
                        }
                    >
                        {formatPercent(clampedProgression, i18n.language)}
                    </span>
                </div>

                <div
                    className={
                        'obj-pillar-progress-track w-full overflow-hidden rounded-full bg-white/35 shadow-inner'
                    }
                    role={'progressbar'}
                    aria-label={t('objectives.pillarPage.overallProgress')}
                    aria-valuenow={clampedProgression}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    data-testid={'pillar-objectives-progress'}
                >
                    <div
                        className={
                            'h-full rounded-full bg-white shadow-sm transition-all duration-700'
                        }
                        style={{ width: `${clampedProgression}%` }}
                    />
                </div>
            </div>
        </section>
    );
}

/**
 * What stands in the banner while its figures are being read: the same frame, drawn as the blocks
 * it is about to hold -- its icon, its title, its counters and its bar -- so no count is shown
 * before it is known.
 */
export function PillarObjectivesHeroSkeleton() {
    const { t } = useTranslation();

    return (
        <div
            role={'status'}
            aria-busy
            aria-label={t('objectives.pillarPage.loading')}
            data-testid={'pillar-objectives-hero-skeleton'}
        >
            <Skeleton className={`${HERO_SURFACE} bg-white/60`}>
                <div className={'obj-pillar-hero-row'}>
                    <Bone
                        color={PLACEHOLDER_BONE_COLOR}
                        className={HERO_ICON_BOX}
                    />

                    <div className={'flex min-w-60 flex-1 flex-col gap-3'}>
                        <Bone
                            color={PLACEHOLDER_BONE_COLOR}
                            className={'h-4 w-32'}
                        />

                        <Bone
                            color={PLACEHOLDER_BONE_COLOR}
                            className={'h-8 w-2/3'}
                        />

                        <div className={'obj-pillar-stats-row mt-3 flex flex-wrap'}>
                            {Array.from({ length: PLACEHOLDER_STAT_COUNT }, (_, index) => (
                                <Bone
                                    key={index}
                                    color={PLACEHOLDER_BONE_COLOR}
                                    className={'h-8 w-32'}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                <Bone
                    color={PLACEHOLDER_BONE_COLOR}
                    className={'obj-pillar-progress-track mt-8 w-full rounded-full'}
                />
            </Skeleton>
        </div>
    );
}
